import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { fetchAllPaginated } from '@/lib/supabase-paginate';
import type { CheckInputs } from '@/lib/maaltall-checks';

type Embedded<T> = T | T[] | null;
const one = <T,>(rel: Embedded<T>): T | null => (Array.isArray(rel) ? rel[0] ?? null : rel);

interface ProjectRef {
  id: string;
  name: string;
  project_code: string | null;
  is_internal: boolean | null;
  deleted_at: string | null;
  project_status: string;
  production_lead?: Embedded<{ full_name: string | null }>;
}

interface AssignmentRow {
  id: string;
  user_id: string;
  hours: number | null;
  budget_line_id: string;
  budget_lines: Embedded<{
    description: string | null;
    cost_type: string | null;
    quantity_hours: number | null;
    projects: Embedded<ProjectRef>;
  }>;
}

const ASSIGNMENT_COLS =
  'id, user_id, hours, budget_line_id, budget_lines(description, cost_type, quantity_hours, projects(id, name, project_code, is_internal, deleted_at, project_status, production_lead:production_lead_id(full_name)))';

/** Bare oppgaver på interne timelinjer i kundeprosjekter som ikke er slettet teller. */
function taskOf(row: AssignmentRow) {
  const line = one(row.budget_lines);
  const project = one(line?.projects ?? null);
  if (!line || !project || project.deleted_at || project.is_internal) return null;
  if (line.cost_type !== 'internal' || Number(line.quantity_hours ?? 0) <= 0) return null;
  return { line, project };
}

export type CheckDbInputs = Pick<
  CheckInputs,
  'draftInvoices' | 'pendingTasks' | 'approvedTasks' | 'timeEntries' | 'customerProjects'
>;

/**
 * Grunnlaget for de faste sjekkene som ikke allerede finnes i andre hooks
 * (VIB-2991): fakturakladder, oppgaver som venter på eller har fått
 * godkjenning, årets timer med kobling til oppgave, og kundeprosjektene.
 * Team-brede lesinger pagineres (1000-raders taket).
 */
export function useMaaltallCheckInputs(year: number, enabled = true) {
  return useQuery({
    queryKey: ['maaltall-check-inputs', { year }],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CheckDbInputs> => {
      const from = `${year}-01-01`;
      const to = `${year + 1}-01-01`;
      const [drafts, pending, approved, entries, projects] = await Promise.all([
        supabase
          .from('invoices')
          .select('id, project_id, amount_excl_vat, invoice_date, projects(name, project_code, estimated_deadline, project_status, deleted_at)')
          .eq('status', 'draft'),
        fetchAllPaginated<AssignmentRow>('budget_line_assignments', ASSIGNMENT_COLS, (q) => q.eq('task_status', 'completed')),
        fetchAllPaginated<AssignmentRow>('budget_line_assignments', ASSIGNMENT_COLS, (q) =>
          q.eq('task_status', 'approved').gte('approved_at', from).lt('approved_at', to),
        ),
        fetchAllPaginated<{
          assignment_id: string | null;
          budget_line_id: string | null;
          user_id: string | null;
          project_id: string | null;
          duration_seconds: number | null;
        }>('time_entries', 'id, assignment_id, budget_line_id, user_id, project_id, duration_seconds', (q) =>
          q.is('deleted_at', null).gte('start_date', from).lt('start_date', to),
        ),
        fetchAllPaginated<ProjectRef>('projects', 'id, name, project_code, is_internal, deleted_at, project_status', (q) =>
          q.is('deleted_at', null),
        ),
      ]);
      if (drafts.error) throw drafts.error;

      const draftInvoices: CheckDbInputs['draftInvoices'] = [];
      for (const inv of drafts.data ?? []) {
        const p = one(inv.projects as Embedded<{ name: string; project_code: string | null; estimated_deadline: string | null; project_status: string; deleted_at: string | null }>);
        if (!p || p.deleted_at || p.project_status !== 'active' || !inv.project_id) continue;
        draftInvoices.push({
          id: inv.id,
          projectId: inv.project_id,
          projectName: `${p.project_code ?? ''} ${p.name}`.trim(),
          amount: Number(inv.amount_excl_vat ?? 0),
          invoiceDate: inv.invoice_date,
          projectDeadline: p.estimated_deadline,
        });
      }

      const pendingTasks: CheckDbInputs['pendingTasks'] = [];
      for (const row of pending) {
        const t = taskOf(row);
        if (!t) continue;
        pendingTasks.push({
          id: row.id,
          projectId: t.project.id,
          projectName: t.project.name,
          label: t.line.description ?? 'Oppgave',
          hours: Number(row.hours ?? 0),
          leadName: one(t.project.production_lead ?? null)?.full_name ?? null,
        });
      }

      const approvedTasks: CheckDbInputs['approvedTasks'] = [];
      for (const row of approved) {
        const t = taskOf(row);
        if (!t) continue;
        approvedTasks.push({
          id: row.id,
          budgetLineId: row.budget_line_id,
          userId: row.user_id,
          projectId: t.project.id,
          projectName: t.project.name,
          label: t.line.description ?? 'Oppgave',
          hours: Number(row.hours ?? 0),
        });
      }

      return {
        draftInvoices,
        pendingTasks,
        approvedTasks,
        timeEntries: entries.map((e) => ({
          assignmentId: e.assignment_id,
          budgetLineId: e.budget_line_id,
          userId: e.user_id,
          projectId: e.project_id,
          hours: Number(e.duration_seconds ?? 0) / 3600,
        })),
        customerProjects: new Map(
          projects.filter((p) => !p.is_internal).map((p) => [p.id, `${p.project_code ?? ''} ${p.name}`.trim()]),
        ),
      };
    },
  });
}
